import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Card,
  CardContent,
  CardActions,
  Grid,
  IconButton,
  Tooltip,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Preview as PreviewIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import Editor from './components/Editor';
import axios from '../../utils/axios';
import { useTranslation } from 'react-i18next';

const getMonthInFrench = () => {
  const months = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];
  return months[new Date().getMonth()];
};

const defaultTemplate = `
<h1 style="text-align: center;">La Yoga Yourte du Luberon</h1>
<h2 style="text-align: center;">HARI OM</h2>
<h2 style="text-align: center;">Shanti Shanti Shanti</h2>
`;

const NewsletterManagement = () => {
  const { t } = useTranslation();
  const [newsletters, setNewsletters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openPreview, setOpenPreview] = useState(false);
  const [selectedNewsletter, setSelectedNewsletter] = useState(null);
  const [subscriberCount, setSubscriberCount] = useState(0);
  const [formData, setFormData] = useState({
    title: '',
    subject: '',
    content: '',
  });
  const [openSendDialog, setOpenSendDialog] = useState(false);
  const [newsletterToSend, setNewsletterToSend] = useState(null);

  useEffect(() => {
    fetchNewsletters();
    fetchSubscriberCount();
  }, []);

  const fetchSubscriberCount = async () => {
    try {
      const response = await axios.get('/api/newsletters/subscriber-count');
      setSubscriberCount(response.data.count);
    } catch (error) {
      console.error('Error fetching subscriber count:', error);
    }
  };

  const fetchNewsletters = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/newsletters');
      setNewsletters(response.data);
    } catch (error) {
      setError(t('errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field) => (event) => {
    setFormData({ ...formData, [field]: event.target.value });
  };

  const handleEditorChange = (content) => {
    setFormData({ ...formData, content });
  };

  const handleSubmit = async () => {
    try {
      if (!formData.title || !formData.subject || !formData.content) {
        setError(t('required'));
        return;
      }

      setLoading(true);
      if (selectedNewsletter) {
        const response = await axios.put(`/api/newsletters/${selectedNewsletter._id}`, formData);
      } else {
        const response = await axios.post('/api/newsletters', { 
          ...formData,
          status: 'draft'
        });
        setFormData({ title: '', subject: '', content: '' });
      }
        
        setOpenDialog(false);
        await fetchNewsletters();
      setSelectedNewsletter(null);
    } catch (error) {
      setError(t('errorSaving'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/api/newsletters/${id}`);
      await fetchNewsletters();
    } catch (error) {
      setError(t('errorDeleting'));
    }
  };

  const handleSendClick = (newsletter) => {
    setNewsletterToSend(newsletter);
    setOpenSendDialog(true);
  };

  const handleSendConfirm = async () => {
    if (subscriberCount === 0) {
      setError(t('noSubscribers'));
      setOpenSendDialog(false);
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`/api/newsletters/${newsletterToSend._id}/send`);
      
      // Update the newsletter in the list with the response data
      setNewsletters(newsletters.map(n => 
        n._id === newsletterToSend._id 
          ? { ...n, ...response.data.newsletter }
          : n
      ));
      
      setOpenSendDialog(false);
      setNewsletterToSend(null);
    } catch (error) {
      setError(error.response?.data?.message || t('errorSending'));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleCreateNewClick = () => {
    setSelectedNewsletter(null);
    setFormData({
      title: '',
      subject: '',
      content: defaultTemplate
    });
    setOpenDialog(true);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" component="h1">
          {t('newsletterManagement')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
            onClick={handleCreateNewClick}
        >
          {t('createNewsletter')}
        </Button>
      </Box>
        <Typography color="text.secondary">
          {t('subscriberCount')}: {subscriberCount}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && <CircularProgress sx={{ display: 'block', mx: 'auto', my: 4 }} />}

      <Grid container spacing={3}>
        {newsletters.map((newsletter) => (
          <Grid item xs={12} md={6} key={newsletter._id}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Typography variant="h6">
                    {newsletter.title}
                  </Typography>
                  <Chip
                    label={newsletter.status === 'sent' ? t('sent') : t('notSent')}
                    color={newsletter.status === 'sent' ? "success" : "default"}
                    size="small"
                  />
                </Box>
                <Typography color="text.secondary" gutterBottom>
                  {t('subject')}: {newsletter.subject}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('created')}: {formatDate(newsletter.createdAt)}
                </Typography>
                {newsletter.status === 'sent' && newsletter.sentDate && (
                  <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                    {t('sendDate')}: {formatDate(newsletter.sentDate)}
                  </Typography>
                )}
              </CardContent>
              <CardActions sx={{ p: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  startIcon={<PreviewIcon />}
                  onClick={() => {
                    setSelectedNewsletter(newsletter);
                    setOpenPreview(true);
                  }}
                >
                  Aperçu
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => {
                    setSelectedNewsletter(newsletter);
                    setFormData({
                      title: newsletter.title,
                      subject: newsletter.subject,
                      content: newsletter.content,
                    });
                    setOpenDialog(true);
                  }}
                >
                  Modifier
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SendIcon />}
                  onClick={() => handleSendClick(newsletter)}
                  color="primary"
                >
                  Envoyer
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DeleteIcon />}
                  onClick={() => handleDelete(newsletter._id)}
                  color="error"
                >
                  Supprimer
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Create/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedNewsletter ? t('edit') : t('createNewsletter')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label={t('title')}
              value={formData.title}
              onChange={handleInputChange('title')}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label={t('subject')}
              value={formData.subject}
              onChange={handleInputChange('subject')}
              margin="normal"
              required
            />
            <Box sx={{ mt: 2 }}>
              <Editor
                value={formData.content}
                onChange={handleEditorChange}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>
            {t('cancel')}
          </Button>
          <Button 
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
          >
            {selectedNewsletter ? t('save') : t('createNewsletter')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog
        open={openPreview}
        onClose={() => setOpenPreview(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {t('preview')}: {selectedNewsletter?.title}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              {t('subject')}: {selectedNewsletter?.subject}
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              {t('recipients')}: {subscriberCount}
            </Typography>
            <Box
              sx={{
                mt: 2,
                '& img': { maxWidth: '100%', height: 'auto' }
              }}
              dangerouslySetInnerHTML={{ __html: selectedNewsletter?.content }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPreview(false)}>
            {t('close')}
          </Button>
          {selectedNewsletter?.status !== 'sent' && (
            <Button
              variant="contained"
              onClick={() => {
                setOpenPreview(false);
                handleSendClick(selectedNewsletter);
              }}
              disabled={subscriberCount === 0}
            >
              {subscriberCount === 0 ? t('noSubscribers') : t('send')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Send Confirmation Dialog */}
      <Dialog
        open={openSendDialog}
        onClose={() => setOpenSendDialog(false)}
      >
        <DialogTitle>
          {t('confirmSend')}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {t('confirmSendMessage')}
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            {t('recipients')}: {subscriberCount}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenSendDialog(false)}>
            {t('cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSendConfirm}
            disabled={loading || subscriberCount === 0}
            color="primary"
          >
            {t('send')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default NewsletterManagement; 